import { buildingGroundZ } from "building-generation/elements/level/buildingGroundZ";
import type { Building } from "building-types/building/buildingTypes";
import type { DrawingCamera } from "doc/plans/drawingCameraTypes";
import { DrawingCameraType } from "doc/plans/drawingCameraTypes";
import {
  builtInElevationVisibilitySelectors,
  builtInPerspectiveVisibilitySelectors,
} from "doc/plans/SheetGeometrySelectorEffectMap";
import { aabb2AddPoint } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { aabb3AddPoint } from "geom/aabb3";
import type { AABB3 } from "geom/aabb3Types";
import { aabb3New } from "geom/aabb3Types";
import type { Face } from "geom/brep";
import type { Mat4 } from "geom/mat4";
import { mat4Dot } from "geom/mat4";
import { vertexArrayFromFaces } from "geom/tess";
import { drawingCameraToMatrices } from "lib/house/plans/drawingCameraToMatrices";
import { renderBuildingOutlineFaces } from "./buildingOutlineRenderer";

export function getNDCBoundingBox(
  cameraNear: number,
  viewMatrix: Mat4,
  projectionMatrix: Mat4,
  vertexArray: number[] | Float32Array,
  minWorldZ: number,
): AABB2 {
  const bb = aabb2New(Infinity, Infinity, -Infinity, -Infinity);
  const negativeCameraNear = -cameraNear;
  for (let i = 0, l = vertexArray.length / 3; i < l; ++i) {
    const worldX = vertexArray[i * 3];
    const worldY = vertexArray[i * 3 + 1];
    const worldZ = Math.max(vertexArray[i * 3 + 2], minWorldZ);
    const [viewX, viewY, viewZ] = mat4Dot(viewMatrix, worldX, worldY, worldZ);
    if (viewZ < negativeCameraNear) {
      const [ndcX, ndcY] = mat4Dot(projectionMatrix, viewX, viewY, viewZ);
      aabb2AddPoint(bb, ndcX, ndcY);
    }
  }
  return bb;
}

interface RenderNDCBBFromBuildingOptions {
  building: Building;
  camera: DrawingCamera;
}

export function renderNDCBBFromBuildingVertices({
  cameraNear,
  viewMatrix,
  isPerspectiveMatrix,
  projectionMatrix,
  building,
  vertexArray,
}: {
  cameraNear: number;
  viewMatrix: Mat4;
  isPerspectiveMatrix: boolean;
  projectionMatrix: Mat4;
  building: Building;
  vertexArray: number[] | Float32Array;
}) {
  let minWorldZ = -Infinity;
  if (isPerspectiveMatrix) {
    // Clamp all vertices to the ground plane when doing a perspective render.
    minWorldZ = buildingGroundZ(building);
  }

  return getNDCBoundingBox(cameraNear, viewMatrix, projectionMatrix, vertexArray, minWorldZ);
}

export function getViewSpaceBoundingBox(viewMatrix: Mat4, vertexArray: number[] | Float32Array): AABB3 {
  const bb = aabb3New(Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity);
  for (let i = 0, l = vertexArray.length / 3; i < l; ++i) {
    const worldX = vertexArray[i * 3];
    const worldY = vertexArray[i * 3 + 1];
    const worldZ = vertexArray[i * 3 + 2];
    const [viewX, viewY, viewZ] = mat4Dot(viewMatrix, worldX, worldY, worldZ);
    aabb3AddPoint(bb, viewX, viewY, viewZ);
  }
  return bb;
}

export function renderViewSpaceBBFromVertices({ viewMatrix, faces }: { viewMatrix: Mat4; faces: Face[] }): AABB3 {
  const vertexArray = vertexArrayFromFaces(faces);
  return getViewSpaceBoundingBox(viewMatrix, vertexArray);
}

/**
 * @returns Rendered bounding box of the building in normalized device coordindates (range -1.0 to 1.0 on both dimensions).
 */
export async function renderNDCBBFromBuilding({ building, camera }: RenderNDCBBFromBuildingOptions): Promise<AABB2> {
  const faces = await renderBuildingOutlineFaces(
    building,
    camera.type === DrawingCameraType.ORTHOGRAPHIC
      ? builtInElevationVisibilitySelectors()
      : builtInPerspectiveVisibilitySelectors(),
  );
  const vertexArray = vertexArrayFromFaces(faces);

  const { viewMatrix, projectionMatrix } = drawingCameraToMatrices(camera);
  return renderNDCBBFromBuildingVertices({
    cameraNear: camera.near,
    viewMatrix,
    isPerspectiveMatrix: camera.type === DrawingCameraType.PERSPECTIVE,
    projectionMatrix,
    building,
    vertexArray,
  });
}
